import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {Card, CardHeader, CardBody, Button, Avatar, Tabs, Tab, Divider, DropdownMenu, Dropdown, DropdownTrigger, Input, DropdownItem, RadioGroup} from "@heroui/react";
import MainStore from "./MainStore";
import { useNavigate } from "react-router-dom";
import useDataEvent from "./CustomHook";
import { useCompanyLogo } from "./@components/CompanyLogo";
import { useSmoothGradient } from "./@components/SmoothGradient";
import { DebouncedInput } from "./@components/DebouncedInput";
import { Icon } from "@iconify/react/dist/iconify.js";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  format,
  parseISO,
  addMonths,
  startOfToday,
} from 'date-fns'
import CheckoutButton from "./@components/CheckoutButton";
import BillingPortalButton from "./@components/BillingPortalButton";
import PlanRadio from "./@components/plan-radio";

 const permissionLabels = {
    "viewer": "Viewer",
    "editor": "Editor",
    "admin": "Admin",
  };

export default function AccountScreen(){    
    const mainstore = MainStore()
    const navigate = useNavigate()        
    const [, forceUpdate] = useReducer(x=>x+1)
    const {logo, color} = useCompanyLogo( {url: mainstore.activeOrganization?.avatarUrl } )
    const {gradient} = useSmoothGradient(color ?? "slate", 400);
    const [companyName, setCompanyName] = useState( mainstore.activeOrganization?.name )
    const [usageRange, setUsageRange] = useState("this_week")
    const [usageSummary, setUsageSummary] = useState(null)
    const [usageLoading, setUsageLoading] = useState(false)
    const [usageError, setUsageError] = useState("")
    const [selectedKeys, setSelectedKeys] = React.useState(new Set(["viewer"]));
    const [localActivePlanId, setLocalActivePlanId] = React.useState( mainstore.activeOrganization.activePlanId )
    const [inviteEmailInput, setInviteEmailInput] = useState("")
    const [invitePending, setInvitePending] = useState(false)
    const [inviteError, setInviteError] = useState("")
    const [inviteResult, setInviteResult] = useState("")

    useDataEvent("organization_updated", mainstore.activeOrganization.id)
    

    const organizationInfo = mainstore.activeOrganization ?? []
    const isExternalUser = Boolean(mainstore.activeUser?.external)
    const canInviteMembers = ["owner", "admin"].includes(mainstore.activeUser?.role)
    const seatLimit = Number.isFinite(Number(organizationInfo?.seatLimit)) && Number(organizationInfo?.seatLimit) > 0
      ? Math.floor(Number(organizationInfo.seatLimit))
      : 1
    const seatsUsed = Array.isArray(organizationInfo?.members) ? organizationInfo.members.length : 0
    const seatsRemaining = Math.max(0, seatLimit - seatsUsed)
    const planInfo = organizationInfo.plan ?? {}
    const today = startOfToday()
    const activeOrganizationId = mainstore.activeOrganization?.id ?? mainstore.activeOrganization?._id

    useEffect(()=>{
      if( !activeOrganizationId ){
        setUsageSummary(null)
        return
      }

      let didCancel = false
      setUsageLoading(true)
      setUsageError("")
      fetch(`/api/organizations/${activeOrganizationId}/usage?range=${usageRange}`, {
        method: "get"
      })
        .then(async (response)=>{
          const body = await response.json().catch(()=>({}))
          if( !response.ok ){
            throw new Error(body?.error ?? `Failed to fetch usage (${response.status})`)
          }
          return body
        })
        .then((body)=>{
          if( didCancel ){
            return
          }
          setUsageSummary(body)
        })
        .catch((error)=>{
          if( didCancel ){
            return
          }
          setUsageError(error?.message ?? "Unable to fetch usage")
          setUsageSummary(null)
        })
        .finally(()=>{
          if( !didCancel ){
            setUsageLoading(false)
          }
        })

      return ()=>{
        didCancel = true
      }
    }, [activeOrganizationId, usageRange])

    const usageData = useMemo(()=>{
      const resolution = usageSummary?.resolution
      const buckets = usageSummary?.buckets ?? []
      return buckets.map((bucket)=>{
        const bucketDate = bucket?.bucketStart ? parseISO(bucket.bucketStart) : null
        const label = bucketDate
          ? resolution === "month"
            ? format(bucketDate, "MMM yyyy")
            : resolution === "week"
            ? `Week of ${format(bucketDate, "MMM d")}`
            : format(bucketDate, "MMM d")
          : ""
        return {
          label,
          flowRuns: Number(bucket?.flowRuns ?? 0),
        }
      })
    }, [usageSummary])

    const userInfo = useMemo(()=>{
        return organizationInfo.members.map(member=>{
            const user = mainstore.users().find(d=>d._id === member.userId)
            return {
                id: member.userId,
                avatar: user?.avatarUrl,
                name: user?.name ?? "Anonymous user",
                email: user?.email ?? "Anonymous email",
                role: member.role
            }
        })
    }, [organizationInfo.members, mainstore])

    useDataEvent('new_primitive delete_primitive',undefined, forceUpdate)

    const refreshOrganizationAndUsers = useCallback(async ()=>{
      const [organizations, users] = await Promise.all([
        fetch(`/api/organizations`, { method: "get" }).then((response)=>response.json()),
        fetch(`/api/users`, { method: "get" }).then((response)=>response.json())
      ])
      if( Array.isArray(organizations) && organizations.length > 0 ){
        mainstore.data.organizations = organizations.reduce((acc, org)=>{
          const id = org.id ?? org._id
          if( id ){
            acc[id] = org
          }
          return acc
        }, {})
        const activeOrgId = mainstore.activeOrganization?.id ?? organizations[0]?.id ?? organizations[0]?._id
        if( activeOrgId ){
          mainstore.activeOrganization = mainstore.data.organizations[activeOrgId] ?? organizations[0]
          mainstore.triggerCallback("organization_updated", [activeOrgId], mainstore.activeOrganization, true)
        }
      }
      if( Array.isArray(users) ){
        mainstore.data.users = users.map((user)=>({
          ...user,
          id: user._id
        }))
      }
    }, [mainstore])

    const handleInviteUsers = useCallback(async ()=>{
      const organizationId = mainstore.activeOrganization?.id
      if( !organizationId ){
        setInviteError("No active organization selected")
        return
      }
      const role = Array.from(selectedKeys)[0] ?? "viewer"
      const emails = inviteEmailInput
        .split(/[,\n; ]+/)
        .map((value)=>value.trim().toLowerCase())
        .filter(Boolean)

      if( emails.length === 0 ){
        setInviteError("Enter at least one email address")
        return
      }

      setInvitePending(true)
      setInviteError("")
      setInviteResult("")
      try{
        const inviteResults = await Promise.all(emails.map(async (email)=>{
          const response = await fetch(`/api/organizations/${organizationId}/invitations`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              email,
              role
            })
          })
          const body = await response.json().catch(()=>({}))
          if( !response.ok ){
            throw new Error(body?.error ?? `Invite failed (${response.status})`)
          }
          return body
        }))
        await refreshOrganizationAndUsers()
        setInviteEmailInput("")
        const attachedCount = inviteResults.filter((entry)=>entry?.mode === "attached_existing_user").length
        const pendingCount = inviteResults.length - attachedCount
        const segments = []
        if( attachedCount > 0 ){
          segments.push(`${attachedCount} existing user${attachedCount > 1 ? "s" : ""} linked`)
        }
        if( pendingCount > 0 ){
          segments.push(`${pendingCount} invite${pendingCount > 1 ? "s" : ""} created`)
        }
        setInviteResult(segments.join(", ") || "Invite sent")
      }catch(error){
        setInviteError(error?.message ?? "Unable to invite user")
      }finally{
        setInvitePending(false)
      }
    }, [inviteEmailInput, mainstore.activeOrganization?.id, refreshOrganizationAndUsers, selectedKeys])


    let next = new Date(today.getFullYear(), today.getMonth(), planInfo.renews_day)
    if (next < today) {
        next = addMonths(next, 1)
    }

    //const renewMesage = `Renews on the ${format(nextRenewal, "do")} of each month  (${daysAway} ${daysAway > 1 ? "days" : "day"} away)`

    return ( 
    <div className="flex h-full  w-full items-start justify-center overflow-scroll">
      <Card className="md:my-10 w-full md:max-w-3xl lg:max-w-4xl">
        <CardHeader 
            className="relative flex h-[100px] flex-col justify-end overflow-visible "
             style={{ backgroundImage: gradient }}
             >
          <Avatar
            className="h-20 w-20 translate-y-12"
            src={logo?.src}
          />
        </CardHeader>
        <CardBody>
              <div className="flex flex-col w-full space-y-2 mt-12 px-3 mb-6">
                  <div className="w-full grid grid-cols-[1fr_3fr_min-content] gap-2">
                    <p className="text-small text-default-500">Details</p>
                    <DebouncedInput Input
                        label="Name"
                        size="sm"
                        fullWidth={true}
                        labelPlacement="outside"
                        value={companyName}
                        classNames={{
                            mainWrapper: "w-full",
                            inputWrapper:"shadow-none",
                        }}
                        onChange={value => setCompanyName(value)}
                        placeholder="Enter Company"
                    />
                    <Button size="sm" isDisabled variant="flat" fullWidth={true} className="place-self-end">Update</Button>
                    {!isExternalUser && (
                      <>
                        <Divider className="col-span-3 my-3"/>
                        <p className="text-small text-default-500 row-span-2">Plan</p>

                        <RadioGroup aria-label="Plans" classNames={{wrapper: "gap-3"}} value={localActivePlanId} onValueChange={setLocalActivePlanId}>
                            {(mainstore.activeOrganization?.validPlans ?? []).map(plan=>
                                <PlanRadio
                                    description={plan.featureDescription}
                                    current={mainstore.activeOrganization.activePlanId === plan._id}
                                    icon={
                                        <Icon className="text-secondary" icon="solar:box-minimalistic-linear" width={18} />
                                    }
                                    label={plan.name}
                                    monthlyPrice={plan.price}
                                    value={plan._id}
                                />
                            )}
                        </RadioGroup>

                        <CheckoutButton priceId={mainstore.activeOrganization?.validPlans.find(d=>d._id === localActivePlanId)?.stripe?.priceId} isDisabled={localActivePlanId === mainstore.activeOrganization.activePlanId} size="sm"/>
                        {mainstore.activeOrganization.billing?.stripe?.subscriptionId &&  <BillingPortalButton/>}
                        <Divider className="col-span-3 my-3"/>
                        <Button onPress={()=>navigate("/integrations")}>Integrations</Button>
                        <Divider className="col-span-3 my-3"/>
                      </>
                    )}
                    <p className="text-small text-default-500">Users</p>
                    <p className="col-start-2 col-span-1 text-xs text-default-400 self-center">
                      Seats: {seatsUsed}/{seatLimit} used ({seatsRemaining} remaining)
                    </p>
                    <div className="col-start-2 col-span-1">
                        <Input
                            value={inviteEmailInput}
                            onValueChange={setInviteEmailInput}
                            size="sm"
                            classNames={{
                                inputWrapper:"shadow-none",
                                helperWrapper: "absolute -bottom-6",
                            }}
                            endContent={
                                <Dropdown>
                                    <DropdownTrigger>
                                        <Button
                                        className="text-default-500"
                                        endContent={
                                            <span className="hidden sm:flex">
                                            <Icon icon="solar:alt-arrow-down-linear" />
                                            </span>
                                        }
                                        size="sm"
                                        variant="light"
                                        >
                                        {Array.from(selectedKeys)
                                            .map((key) => permissionLabels[key])
                                            .join(", ")}
                                        </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu
                                        selectedKeys={selectedKeys}
                                        selectionMode="single"
                                        onSelectionChange={setSelectedKeys}
                                    >
                                        {Object.entries(permissionLabels).map(([k,v])=>
                                            <DropdownItem key={k}>{v}</DropdownItem>
                                        )}
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            label="Email Address"
                            labelPlacement="outside"
                            name="email"
                            placeholder="Email comma separated"
                            type="text"
                        />
                        <div className="grid p-3 grid-cols-[1fr_min-content_min-content] my-1">
                            {userInfo.map(user=>
                                <Fragment key={user.id}>
                                    <div className="flex items-center gap-2">
                                        <Avatar size="sm" src={user.avatar} />
                                        <p className="text-small text-default-500">{user.name}</p>
                                    </div>
                                    <p className="text-small text-default-400 place-content-center">{user.role}</p>
                                    <Icon icon="solar:trash-bin-trash-linear" className="place-self-center ml-2 text-default-400" />
                                    <Divider className="col-span-3 my-1.5"/>
                                </Fragment>
                            )}
                        </div>
                    </div>
                    <Button
                      className="col-start-3 self-start mt-[1.375rem]"
                      size="sm"
                      variant="flat"
                      isLoading={invitePending}
                      isDisabled={invitePending || !inviteEmailInput.trim() || !canInviteMembers}
                      onPress={handleInviteUsers}
                    >
                      Invite
                    </Button>
                    {!canInviteMembers && (
                      <p className="col-start-2 col-span-2 text-xs mt-1 text-default-400">
                        Only organization owners and admins can invite members.
                      </p>
                    )}
                    {(inviteError || inviteResult) && (
                      <p className={`col-start-2 col-span-2 text-xs mt-1 ${inviteError ? "text-danger" : "text-success"}`}>
                        {inviteError || inviteResult}
                      </p>
                    )}
                    <Divider className="col-span-3 my-3"/>
                    <p className="text-small text-default-500">Usage</p>
                    <div className="col-start-2 col-span-2">
                      <Tabs
                        size="sm"
                        selectedKey={usageRange}
                        onSelectionChange={(key)=>setUsageRange(`${key}`)}
                      >
                        <Tab key="this_week" title="This week" />
                        <Tab key="last_30_days" title="Last 30 days" />
                        <Tab key="last_year" title="Last year" />
                      </Tabs>
                    </div>
                    <p className="col-start-2 col-span-2 text-xs text-default-400">
                      Resolution: {usageSummary?.resolution ?? "day"}
                    </p>
                    {usageError && (
                      <p className="col-start-2 col-span-2 text-xs text-danger">{usageError}</p>
                    )}
                    <ResponsiveContainer width="100%" height={300} className="col-start-2 col-span-2">
                        <BarChart
                            data={usageData}
                            margin={{
                                top: 5,
                                right: 20,
                                left: 8,
                                bottom: 8,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="flowRuns" fill="#0ea5e9" name="Flows run" />
                        </BarChart>
                    </ResponsiveContainer>
                    {usageLoading && (
                      <p className="col-start-2 col-span-2 text-xs text-default-400">Loading usage data...</p>
                    )}
                </div>
            </div>
        </CardBody>
      </Card>
    </div>
  )
}
